import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";

export interface CrmEventActionAutomationPolicy {
  source: "default" | "saved";
  enabled: boolean;
  decision: "accepted" | "dismissed";
  minConfidence: number;
  days: number;
  limit: number;
  includeManualReview: boolean;
  includeBlocked: boolean;
  eventTypes: string[];
  signalFamilies: string[];
  sourceSurface: string;
  availableEventTypes: string[];
  availableSignalFamilies: string[];
}

export type CrmEventActionAutomationPolicyUpdate = Omit<
  CrmEventActionAutomationPolicy,
  "source" | "availableEventTypes" | "availableSignalFamilies"
>;

export async function updateCrmEventActionAutomationPolicy(
  input: CrmEventActionAutomationPolicyUpdate,
): Promise<CrmEventActionAutomationPolicy> {
  const response = await apiFetch<{ data: CrmEventActionAutomationPolicy }>(
    "/crm/intelligence/event-action-score/recommendations/automation-policy",
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export function useCrmEventActionAutomationPolicy() {
  const [data, setData] = useState<CrmEventActionAutomationPolicy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPolicy = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: CrmEventActionAutomationPolicy }>(
        "/crm/intelligence/event-action-score/recommendations/automation-policy",
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load event action automation policy");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const updatePolicy = useCallback(async (input: CrmEventActionAutomationPolicyUpdate) => {
    const nextPolicy = await updateCrmEventActionAutomationPolicy(input);
    setData(nextPolicy);
    return nextPolicy;
  }, []);

  return { data, isLoading, error, refetch: fetchPolicy, updatePolicy };
}
